import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function CoachLogin({ setUser }) {
  const navigate = useNavigate();
  const [organizationId, setOrganizationId] = useState(localStorage.getItem('organization_id') || '');
  const [checkingDomain, setCheckingDomain] = useState(true);
  const [allowManualOrgInput, setAllowManualOrgInput] = useState(false);
  const [domainError, setDomainError] = useState('');
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    organization_id: ''
  });
  const [loading, setLoading] = useState(false);

  // 檢查網域對應的組織
  useEffect(() => {
    const checkDomainOrganization = async () => {
      // 如果已有 organization_id，直接使用
      const storedOrgId = localStorage.getItem('organization_id');
      if (storedOrgId) {
        setOrganizationId(storedOrgId);
        setFormData(prev => ({ ...prev, organization_id: storedOrgId }));
        setCheckingDomain(false);
        return;
      }

      // 否則從當前網域獲取組織
      const currentDomain = window.location.hostname;
      console.log('CoachLogin: Checking domain:', currentDomain);
      
      // 判斷是否為預覽/本地環境
      const isPreviewOrLocal = currentDomain.includes('localhost') || 
                               currentDomain.includes('127.0.0.1') ||
                               currentDomain.includes('.stage-preview.') ||
                               currentDomain.includes('emergentagent.com');
      
      try {
        const res = await axios.get(`${API}/organizations/by-domain/${currentDomain}`);
        
        if (res.data.found && res.data.organization) {
          const org = res.data.organization;
          console.log('CoachLogin: Found organization:', org.organization_id);
          localStorage.setItem('organization_id', org.organization_id);
          localStorage.setItem('currentOrg', JSON.stringify(org));
          setOrganizationId(org.organization_id);
          setFormData(prev => ({ ...prev, organization_id: org.organization_id }));
          setCheckingDomain(false);
        } else {
          // 自訂網域但找不到對應機構 - 允許手動輸入
          if (!isPreviewOrLocal) {
            console.log('CoachLogin: Custom domain detected but no organization found:', currentDomain);
            setDomainError(`網域 ${currentDomain} 尚未設定對應機構`);
            setAllowManualOrgInput(true);
            setCheckingDomain(false);
          } else {
            // 預覽/本地環境，需要先選擇機構
            console.log('CoachLogin: Preview/local environment, redirect to home');
            toast.error('請先選擇機構');
            navigate('/');
          }
        }
      } catch (error) {
        console.error('CoachLogin: Error checking domain:', error);
        // API 錯誤時的處理
        if (!isPreviewOrLocal) {
          // 自訂網域上發生錯誤，允許手動輸入
          setDomainError('無法驗證網域，請手動輸入機構編號');
          setAllowManualOrgInput(true);
          setCheckingDomain(false);
        } else {
          // 預覽/本地環境，跳轉到首頁
          toast.error('請先選擇機構');
          navigate('/');
        }
      }
    };

    checkDomainOrganization();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    console.log('CoachLogin: Attempting login with:', {
      username: formData.username,
      organization_id: formData.organization_id,
      hasPassword: !!formData.password
    });

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      console.log('CoachLogin: Login response received:', {
        hasToken: !!response.data.access_token,
        userRole: response.data.user?.role,
        userId: response.data.user?.user_id
      });
      
      const userData = response.data.user;
      
      // Strict role validation: only coaches allowed
      if (userData.role !== 'coach') {
        localStorage.removeItem('token');
        const roleText = 
          userData.role === 'student' ? '學生' :
          userData.role === 'parent' ? '家長' :
          userData.role === 'org_admin' ? '機構管理員' :
          userData.role === 'super_admin' ? '超級管理員' : '其他';
        toast.error(`此帳號是${roleText}帳號，請使用對應的登入頁面`);
        console.log('CoachLogin: Wrong role, not navigating');
        setLoading(false);
        return;
      }

      localStorage.setItem('token', response.data.access_token);
      // 確保 organization_id 保存到 localStorage (尤其是手動輸入模式)
      if (formData.organization_id) {
        localStorage.setItem('organization_id', formData.organization_id);
      }
      console.log('CoachLogin: Setting user and navigating to dashboard');
      setUser(userData);
      toast.success('登入成功！');
      navigate('/coach-dashboard');
    } catch (error) {
      console.error('CoachLogin: Login error:', error.response?.status, error.response?.data);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail === 'Account pending approval' 
          ? '您的帳號正在等待審批' 
          : '您的帳號已被拒絕');
      } else {
        toast.error(error.response?.data?.detail || '登入失敗，請檢查您的帳號和密碼');
      }
    } finally {
      setLoading(false);
    }
  };

  // 正在檢查網域時顯示載入狀態
  if (checkingDomain) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 p-4">
        <div className="text-center text-white">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg">正在載入...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">教練登入</h1>
          <p className="text-gray-600 mt-2">歡迎回來！請登入您的教練帳號</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 網域錯誤提示 */}
          {domainError && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">{domainError}</p>
              <p className="text-xs text-amber-600 mt-1">請手動輸入機構編號後登入</p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              機構編號
            </label>
            {allowManualOrgInput ? (
              /* 允許手動輸入模式 */
              <Input
                type="text"
                placeholder="請輸入機構編號"
                value={formData.organization_id}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, organization_id: value });
                  setOrganizationId(value);
                }}
                required
                className="w-full"
                data-testid="organization-id-input"
              />
            ) : (
              /* 鎖定模式 */
              <div className="relative">
                <Input
                  type="text"
                  value={formData.organization_id}
                  readOnly
                  disabled
                  className="w-full bg-gray-100 cursor-not-allowed"
                  data-testid="organization-id-locked"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {allowManualOrgInput ? '請輸入您所屬機構的編號' : '機構編號已鎖定，如需更改請返回首頁'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              教練帳號
            </label>
            <Input
              type="text"
              placeholder="請輸入教練帳號"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              密碼
            </label>
            <Input
              type="password"
              placeholder="請輸入密碼"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              className="w-full"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium py-3 rounded-lg"
          >
            {loading ? '登入中...' : '登入'}
          </Button>
        </form>

        <div className="mt-6 text-center space-y-2">
          <button
            onClick={() => {
              localStorage.removeItem('organization_id');
              navigate('/');
            }}
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
          >
            更換機構編號
          </button>
        </div>
      </div>
    </div>
  );
}
